import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  History,
  Layers,
  MapPin,
  Package,
  Pencil,
  Plus,
  Radar,
  Repeat,
  Route as RouteIcon,
  Search,
  Trash2,
  User as UserIcon,
  Warehouse,
  X,
  CircleSlash,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "../../../lib/react-leaflet-lite";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { createCircleMarker } from "../../../utils/mapHelpers";
import {
  fetchRecoleccionesContext,
  buildEjecucionIndex,
  retirosDeParada,
  paradaAnulada,
  routeRunsOnDow,
  recurrenceLabel,
  normName,
  ymd,
  parseYmd,
} from "../../../api/recolecciones";
import { useTheme } from "../../../hooks/useTheme";
import { useBasemapTile } from "../../../config/basemaps";
import "./adminRecolecciones.scss";
import { formatNumberCL, formatCLP } from "../../../utils/formatters";
import useAuth from "../../../hooks/useAuth";
import useRoutes from "../../../store/routes";
import useIsMobile from "../../../hooks/useIsMobile";
import { usePdfViewer } from "./usePdfViewer";
import useUrlState from "../../../hooks/useUrlState";
import { ProgramarRecoleccionModal } from "../../../components/recolecciones/ProgramarRecoleccionModal";
import AdminWithdrawalsView from "../adminWithdrawals/adminWithdrawals";
import { deleteRoutesApi } from "../../../api/routes";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { CopyButton } from "../../../components/ui/CopyButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/Tabs";
import { MonitorTab } from "./MonitorTab";
import { RetirosTab } from "./RetirosTab";
import {
  DOW_SHORT,
  MONTH_SHORT,
  formatLargo,
  formatDiaCorto,
  horaDe,
  hhmm,
  iniciales,
} from "./recoleccionesShared";
import { toast } from "../../../utils/toast";
import { useDeclarePageTitle } from "../../../providers/PageTitleProvider";

// Centro por defecto del mapa (Santiago) cuando no hay paradas con coords.
const MAP_DEFAULT_CENTER = [-33.45, -70.66];
const MAP_DEFAULT_ZOOM = 12;
// Tamaño de página del listado de rutas del día (scroll infinito client-side).
const ROUTES_PAGE_SIZE = 8;
// Toggle de la vista vieja "Administrar retiros": oculto por pedido del usuario,
// pero el código queda intacto. Cambiar a `true` para volver a mostrar el switcher.
const SHOW_VISTA_VIEJA = false;

// Normaliza una ventana horaria a ISO ("YYYY-MM-DDTHH:MM:00") para que el drawer
// de edición (que hace slice[11,16]) la lea bien, venga "HH:MM" o ISO del back.
function toIsoTime(v) {
  if (!v) return undefined;
  const s = String(v);
  if (/^\d{2}:\d{2}$/.test(s)) return `2026-01-01T${s}:00`;
  return s;
}

// Metadata de los pines del mapa por estado de la parada. El color sale de los
// tokens de estado del sistema (`--status-*`): así el pin se adapta solo a dark
// mode y al modo daltónico (que re-mapea esos mismos tokens). Como el divIcon de
// Leaflet vive en el DOM dentro de `.leaflet-pane`, hereda las CSS vars del
// documento, por lo que `rgb(var(--status-*))` funciona dentro del HTML inline.
const ESTADO_META = {
  // Labels alineados con la tabla ("Pendiente"/"Realizada"): el mapa y el
  // listado deben nombrar igual el mismo estado o el semáforo no se entiende.
  //
  // `tone` es el modificador de `.rec-badge`. Vive acá y no en cada render para que
  // agregar un estado no obligue a acordarse de tocar tres ternarios sueltos —que es
  // exactamente cómo "anulada" terminó pintándose como "Pendiente".
  programada: { color: "rgb(var(--status-neutral))", label: "Pendiente", tone: "neutral" },
  ejecutada: { color: "rgb(var(--status-success))", label: "Realizada", tone: "success" },
  // `en_curso` y `fallida` salieron del semáforo de PARADA: eran placeholders que el
  // backend nunca emitió, y "fallida" era el nombre provisorio de lo que hoy es
  // "anulada". OJO: `en_curso` sigue existiendo para el estado de RUTA
  // (`EstadoInstancia`, ver RUTA_ESTADO_* en MonitorTab), que sí es real.
  //
  // Si el backend llegara a emitir `fallida` para una parada, cae al fallback de
  // `EstadoBadge` y se pinta como pendiente. Es preferible a mantener viva una
  // categoría que el producto ya no usa.
  // El repartidor llegó y el comercio no tenía pedidos. No se cobra ni se paga, y
  // mañana la parada vuelve a estar pendiente sola. Tono `muted` (contorneado) y no
  // `neutral`, que ya es "Pendiente": son dos cosas distintas y el operador tiene que
  // poder separarlas de un vistazo.
  anulada: { color: "rgb(var(--status-neutral))", label: "Anulada", tone: "muted" },
};

// Badge de estado de una parada. Único lugar que traduce estado → etiqueta + tono.
function EstadoBadge({ estado, t }) {
  const meta = ESTADO_META[estado] ?? ESTADO_META.programada;
  return (
    <span className={`rec-badge rec-badge--${meta.tone} rec-badge--dot`}>
      {t(meta.label)}
    </span>
  );
}

// Pines del mapa: el color solo no comunica (daltonismo, tiles saturados), así
// que el estado se codifica también por FORMA: realizado = disco lleno con ✓,
// pendiente = aro hueco (fondo del tema), anulado = aro hueco con ∕. Anulado y
// pendiente comparten el tono neutral —los dos son "sin retirar"— así que la barra
// es lo único que los separa. Iconos a nivel de módulo
// para que su identidad sea estable entre renders: regenerarlos en cada hover
// disparaba marker.setIcon(), que reemplaza el DOM bajo el cursor, se comía el
// mouseout y dejaba tooltips huérfanos pegados en el mapa.
const PIN_GLYPH_CHECK =
  '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
// Barra diagonal del pin anulado. Mismo lenguaje que el icono CircleSlash que usan
// la tarjeta de filtro y la app del repartidor, para que el estado se reconozca igual
// en las tres superficies. Trazo en color de estado (no blanco): el pin es hueco.
const PIN_GLYPH_SLASH =
  '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--status-neutral))" stroke-width="4.5" stroke-linecap="round" aria-hidden="true"><line x1="19" y1="5" x2="5" y2="19"/></svg>';
const PIN_SIZE = 16;
const PIN_ICONS = {
  programada: createCircleMarker({
    size: PIN_SIZE,
    color: "rgb(var(--bg))",
    borderColor: "rgb(var(--status-neutral))",
    borderWidth: 2.5,
    className: "rec-pin",
  }),
  ejecutada: createCircleMarker({
    size: PIN_SIZE,
    color: "rgb(var(--status-success))",
    borderWidth: 1.5,
    glyph: PIN_GLYPH_CHECK,
    glyphFont: "svg",
    className: "rec-pin",
  }),
  anulada: createCircleMarker({
    size: PIN_SIZE,
    color: "rgb(var(--bg))",
    borderColor: "rgb(var(--status-neutral))",
    borderWidth: 2.5,
    glyph: PIN_GLYPH_SLASH,
    glyphFont: "svg",
    className: "rec-pin",
  }),
};
const pinIcon = (estado) => PIN_ICONS[estado] ?? PIN_ICONS.programada;

// Icono de un SITIO con varios retiros (mismo punto físico): donut cuyo anillo
// reparte realizadas/pendientes con conic-gradient y el conteo al centro. El
// centro usa el fondo del tema, así funciona en dark y en modo daltónico (los
// segmentos salen de los mismos tokens --status-* que los pines).
function createSiteClusterIcon({ count, segments, size = 28 }) {
  const total = segments.reduce((s, x) => s + x.n, 0) || 1;
  let acc = 0;
  const stops = segments
    .filter((s) => s.n > 0)
    .map(({ color, n }) => {
      const from = (acc / total) * 360;
      acc += n;
      const to = (acc / total) * 360;
      return `${color} ${from}deg ${to}deg`;
    })
    .join(", ");
  const html =
    `<div class="rec-pin-cluster" style="width:${size}px;height:${size}px;background:conic-gradient(${stops});">` +
    `<span class="rec-pin-cluster__count">${count}</span></div>`;
  return L.divIcon({
    className: "rec-pin rec-pin--cluster",
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 2],
  });
}

// Los tres tipos de evento que pueden ocurrir en un día, y que la cronología distingue.
// Mezclarlos fue el bug original: una recepción en bodega contada como retiro cumplido.
const TIPO_EVENTO = {
  retiro: { label: "Retiro", dot: "success", tone: "success" },
  recepcion: { label: "Recepción en bodega", dot: "brand", tone: "brand" },
  sin_parada: { label: "Sin parada identificada", dot: "warning", tone: "warning" },
};
const estadoColor = (estado) => (ESTADO_META[estado] ?? ESTADO_META.programada).color;

// ───────────────────────── helpers de fecha ─────────────────────────
// (formatLargo/formatDiaCorto/horaDe/hhmm/iniciales viven en recoleccionesShared:
// también los usan las pestañas Monitoreo y Retiros.)

// Frecuencia del back → recurrencia que entiende el drawer (para precargar).
function frequencyToRecurrencia(freq) {
  if (freq === "Sábado") return { tipo: "semanal", dias: [6] };
  if (freq === "sunday") return { tipo: "semanal", dias: [0] };
  // "Lunes a viernes" (y cualquier otro) → laboral, que es el caso común.
  return { tipo: "laboral" };
}

// ───────────────────────── KPI cards (estados) ─────────────────────────

const ESTADO_CARDS = [
  { value: "", label: "Todas", icon: Layers, tone: "brand" },
  { value: "programada", label: "Programadas", icon: CalendarClock, tone: "neutral" },
  { value: "ejecutada", label: "Realizadas", icon: CheckCircle2, tone: "success" },
  { value: "anulada", label: "Anuladas", icon: CircleSlash, tone: "neutral" },
];

// ───────────────────────── vista principal ─────────────────────────

export default function AdminRecolecciones() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pdfLink, pdfModal } = usePdfViewer();
  // La vista ya se rotula con su propio <h1>: el header de la app no repite.
  useDeclarePageTitle(t("Recolecciones"));
  const { isDark } = useTheme();
  // Tile del basemap activo (fuente única configurable desde Configuración).
  const tile = useBasemapTile(isDark);
  const [routes, setRoutes] = useState([]);
  // Ejecución por fecha: Map<'YYYY-MM-DD', { paradas, recepciones }>.
  const [ejecuciones, setEjecuciones] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  // Filtros y navegación persistidos en el querystring (compartible/bookmarkeable
  // y sincronizado con back/forward del browser). Los defaults son los MISMOS que
  // antes (día = hoy, sin filtro, sin búsqueda, vista "nueva"), así que sin params
  // en la URL el comportamiento es IDÉNTICO al actual; cada param se omite de la URL
  // cuando iguala su default (URL limpia). La lectura inicial es síncrona, por lo que
  // siembra `selectedDate` antes del primer fetch. NO cambia el cálculo de la franja
  // ni los params del store.
  const [search, setSearch] = useUrlState("q", "");
  const [estado, setEstado] = useUrlState("estado", "");
  const [selectedDate, setSelectedDate] = useUrlState("date", ymd(new Date()));
  // Pestaña activa (Planificación | Retiros | Monitoreo), port del mockup
  // mu-portal. Por pedido del usuario la vista aterriza en Planificación (la
  // operación diaria de siempre); los deep-links viejos con ?q=/?estado= caen
  // ahí mismo, donde esos filtros aplican. push:true ⇒ el back/forward del
  // browser vuelve al tab anterior.
  const [tabRaw, setTab] = useUrlState("tab", "planificacion", { push: true });
  // Un ?tab= inválido no debe dejar la página en blanco (ningún panel matchea).
  const tab = ["monitoreo", "retiros", "planificacion"].includes(tabRaw) ? tabRaw : "planificacion";
  // Vista activa: "nueva" (Recolecciones) | "vieja" (Administrar retiros legacy).
  // push:true ⇒ es un tab; el back/forward del browser vuelve al tab anterior.
  const [vista, setVista] = useUrlState("vista", "nueva", { push: true });
  const [collapsed, setCollapsed] = useState(new Set());
  // Teléfono: el navegador de día se colapsa a una sola fila (ver más abajo) y
  // la tira semanal marca con un degradado que hay más días a la derecha.
  const isMobile = useIsMobile(992);
  // 768 (el `md` de Tailwind): solo para las pestañas, que sí caben con sus iconos
  // desde tablet. No se puede resolver con clases responsive porque lo que cambia es
  // si el nodo del icono se renderiza (TabsTrigger recibe el icono como prop).
  const hideTabIcons = useIsMobile();
  const franjaRef = useRef(null);
  // Scroll infinito (windowing) del listado de rutas del día: se pinta por
  // páginas para no renderizar cientos de tablas de golpe. Los contadores de
  // arriba siguen siendo reales (se calculan sobre TODAS las rutas, no sobre lo
  // pintado).
  const [visibleCount, setVisibleCount] = useState(ROUTES_PAGE_SIZE);
  const sentinelRef = useRef(null);
  // "Colectas sin ruta" vive al final de una lista con scroll infinito: sin un atajo,
  // el operador no sabe que existe. El contador de la cabecera lleva hasta acá.
  const colectasRef = useRef(null);

  // Filtros del mapa (independientes del filtro de estado de la página).
  // Capas: estados de parada que se muestran en el mapa. Los tres son reales.
  const [mapLayers, setMapLayers] = useState({
    programada: true,
    ejecutada: true,
    anulada: true,
  });
  const [mapRouteId, setMapRouteId] = useState(""); // "" = todas las rutas
  const [mapPanelOpen, setMapPanelOpen] = useState(false);

  // Drawer "Programar ruta" — abre el modal lateral con el form de plantilla.
  // `editandoRuta` !== null → el drawer abre en modo edición precargado.
  const [programarOpen, setProgramarOpen] = useState(false);
  const [editandoRuta, setEditandoRuta] = useState(null);
  // Eliminación de ruta (plantilla): confirmación + estado de carga.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  // Id de empresa logística: MISMO origen que filterModal/reasignModal (los
  // flujos que SÍ traen choferes). El JWT puede traer `logisticCompany` como
  // OBJETO ({_id,...}) o directamente como STRING (el id), y a veces solo el
  // campo plano `logisticCompanyId`. Cubrimos los tres; el plano de último
  // recurso porque no siempre coincide con el id que espera la API.
  const logisticCompanyId =
    user?.logisticCompany?._id ||
    (typeof user?.logisticCompany === "string" ? user.logisticCompany : null) ||
    user?.logisticCompanyId ||
    null;
  const deliveries = useRoutes((state) => state.deliveries || []);
  const fetchDeliveries = useRoutes((state) => state.fetchDeliveries);
  // Catálogo de clientes activos: alimenta el autocompletado de dirección de
  // cada parada en el drawer de edición (la dirección sale de
  // client.shippingAddresses, no de un endpoint nuevo).
  const clients = useRoutes((state) => state.clients || []);
  const fetchClients = useRoutes((state) => state.fetchClients);
  const [clientsLoading, setClientsLoading] = useState(false);
  useEffect(() => {
    // Cargar repartidores activos para el dropdown del drawer, con el MISMO origen
    // que el filtro de /admin/pedidos: getDeliveryUsers('active','delivery', lcId).
    // Pasar role 'delivery' excluye a propósito a 'delivery_freelance' (evita asignar
    // la ruta de recolección a un freelancer y el lío de homónimos entre roles).
    fetchDeliveries?.('active', 'delivery', logisticCompanyId).catch(() => {});
  }, [fetchDeliveries, logisticCompanyId]);
  useEffect(() => {
    // Cargar clientes activos (con sus direcciones) una vez por empresa.
    if (!logisticCompanyId || clients.length > 0) return;
    setClientsLoading(true);
    Promise.resolve(fetchClients?.(logisticCompanyId))
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  }, [fetchClients, logisticCompanyId, clients.length]);
  // Hubs derivados del JWT: usamos las direcciones de operación con
  // shippingLocation=true (bodegas) — el back todavía no expone catálogo
  // dedicado de hubs, pero esto le da al usuario opciones reales.
  const hubsFromCompany = useMemo(() => {
    const addrs = user?.logisticCompany?.operationsAddresses || [];
    return addrs
      .filter((a) => a.shippingLocation || a.type === 'Bodega')
      .map((a) => ({
        id: a._id,
        nombre: `${a.type || t("Bodega")} — ${a.address}, ${a.municipality}`,
      }));
  }, [user?.logisticCompany?.operationsAddresses, t]);

  const todayYmd = ymd(new Date());

  // Franja de 7 días: 1 atrás + día + 5 adelante (igual que el mockup)
  const fechasFranja = useMemo(() => {
    const center = parseYmd(selectedDate);
    const out = [];
    for (let off = -1; off <= 5; off++) {
      const d = new Date(center);
      d.setDate(d.getDate() + off);
      out.push(ymd(d));
    }
    return out;
  }, [selectedDate]);

  // Rutas: una vez. Pickups: por ventana de la franja.
  // Solo carga cuando la vista nueva está activa: evita pegarle al backend en
  // vano mientras se muestra la vista vieja "Administrar retiros".
  useEffect(() => {
    if (SHOW_VISTA_VIEJA && vista !== "nueva") return undefined;
    let cancel = false;
    setLoading(true);
    fetchRecoleccionesContext({ fechas: fechasFranja }).then(({ routes: r, ejecucionPorFecha }) => {
      if (cancel) return;
      setRoutes(r);
      setEjecuciones(ejecucionPorFecha);
      setLoading(false);
    });
    return () => {
      cancel = true;
    };
  }, [fechasFranja, vista]);

  // Resumen por fecha (para la franja). `retiros` cuenta SOLO retiros de ruta; las
  // recepciones en bodega van aparte porque no son lo mismo: nadie salió a buscarlas.
  const resumenPorFecha = useMemo(() => {
    const out = new Map();
    for (const fecha of fechasFranja) {
      const dow = parseYmd(fecha).getDay();
      const progRoutes = routes.filter((r) => routeRunsOnDow(r, dow));
      const conductores = new Set();
      progRoutes.forEach((r) => r.deliveryManName && conductores.add(r.deliveryManName));
      const ejec = ejecuciones.get(fecha);
      out.set(fecha, {
        rutas: progRoutes.length,
        retiros: ejec?.paradas.length || 0,
        recepciones: ejec?.recepciones.length || 0,
        conductores,
      });
    }
    return out;
  }, [fechasFranja, routes, ejecuciones]);

  // Instancias del día seleccionado = rutas cuya frecuencia aplica al día.
  const dow = parseYmd(selectedDate).getDay();

  // Ejecución del día indexada por parada. El vínculo retiro↔parada lo resuelve el
  // backend por id; acá sólo se busca. Fuente única para el listado y para las KPI
  // cards, así nunca divergen.
  const ejecucionIndex = useMemo(
    () => buildEjecucionIndex(ejecuciones.get(selectedDate)),
    [ejecuciones, selectedDate],
  );

  // Instancias COMPLETAS del día (sin recorte por búsqueda). Cada instancia trae
  // el conjunto TOTAL de sus paradas. Es la fuente de verdad para la edición de la
  // plantilla: el drawer debe precargar todas las paradas, no solo las visibles.
  const instanciasFull = useMemo(() => {
    const progRoutes = routes.filter((r) => routeRunsOnDow(r, dow));
    return progRoutes
      .map((route) => {
        // El back puede entregar los datos por parada dentro de `route.clients[]`
        // o en un arreglo aparte (`route.paradas`/`route.stops`). Indexamos por
        // clientId para fusionar ambos orígenes al construir cada parada.
        const stopsRaw = route.paradas || route.stops || [];
        const stopByClient = new Map();
        stopsRaw.forEach((st) => {
          const cid = st.clientId || st.client?._id || st.client?.id || st.id;
          if (cid != null) stopByClient.set(String(cid), st);
        });
        const paradas = (route.clients || []).map((c) => {
          const s = stopByClient.get(String(c.id || c._id || "")) || {};
          // Retiros de ESTA parada (ruta + comercio + dirección), resueltos por id en el
          // backend. Una parada puede tener más de un retiro el mismo día: `pickup`
          // (singular) = el primero, se conserva por compat (mapa/captura de coords);
          // `pickups` alimenta el render 1-a-muchos.
          const pickups = retirosDeParada(ejecucionIndex, route.id, c);
          const pickup = pickups[0] || null;
          // GeoJSON Point del backend → {lat, lng} para el mapa.
          // `null` cuando la dirección no fue geocodificada o el pickup
          // legacy no incluye location.
          const planned = c.shippingAddress?.location?.coordinates;
          const geoPlanned =
            Array.isArray(planned) &&
            Number.isFinite(planned[0]) &&
            Number.isFinite(planned[1])
              ? { lat: planned[1], lng: planned[0] }
              : null;
          const captured = pickup?.capturedLocation?.coordinates;
          const geoCaptured =
            Array.isArray(captured) &&
            Number.isFinite(captured[0]) &&
            Number.isFinite(captured[1])
              ? { lat: captured[1], lng: captured[0] }
              : null;
          return {
            id: c.id || c._id || c.name,
            // IDs reales para poder editar la ruta contra el back (PATCH
            // routes/:id espera clients:[{id, selectedAddress}], ver editRouteClients).
            clientId: c.id || c._id || null,
            selectedAddress: c.shippingAddress?.id || c.shippingAddress?._id || "",
            comercio: c.name || c.legalName || t("Sin nombre"),
            direccion: c.shippingAddress?.address || "",
            comuna: c.shippingAddress?.municipality || c.shippingAddress?.state || "",
            // Datos operativos por parada. El back los persiste en clients[] y los
            // devuelve en getRoutesWithDetails con nombres canónicos en inglés
            // (window.start/end, expectedBulks, clientCharge, geofencingEnabled…).
            // Se mantienen variantes ES/legacy como fallback defensivo.
            ventanaDesde: toIsoTime(c.window?.start ?? s.ventanaDesde ?? c.ventanaDesde ?? s.pickupWindow?.from ?? c.pickupWindow?.from ?? s.timeWindow?.from ?? c.timeWindow?.from),
            ventanaHasta: toIsoTime(c.window?.end ?? s.ventanaHasta ?? c.ventanaHasta ?? s.pickupWindow?.to ?? c.pickupWindow?.to ?? s.timeWindow?.to ?? c.timeWindow?.to),
            bultosEsperados: s.bultosEsperados ?? c.bultosEsperados ?? s.expectedBulks ?? c.expectedBulks ?? "",
            pedidosEsperados: s.pedidosEsperados ?? c.pedidosEsperados ?? s.expectedOrders ?? c.expectedOrders ?? "",
            pesoEsperadoKg: s.pesoEsperadoKg ?? c.pesoEsperadoKg ?? s.expectedWeightKg ?? c.expectedWeightKg ?? "",
            valorCobrarCliente: s.valorCobrarCliente ?? c.valorCobrarCliente ?? s.clientCharge ?? c.clientCharge ?? "",
            valorPagarRepartidor: s.valorPagarRepartidor ?? c.valorPagarRepartidor ?? s.driverPayment ?? c.driverPayment ?? "",
            umbralCobroPedidos: c.freeOrdersThreshold ?? s.umbralCobroPedidos ?? c.umbralCobroPedidos ?? s.chargeThreshold ?? c.chargeThreshold ?? "",
            geofencingActivo: c.geofencingEnabled ?? s.geofencingActivo ?? c.geofencingActivo ?? s.geofencing ?? c.geofencing ?? true,
            geofencingMetros: c.geofencingMeters ?? s.geofencingMetros ?? c.geofencingMetros ?? s.geofencingRadius ?? c.geofencingRadius ?? 100,
            // La anulación gana sobre "programada", pero NO sobre un retiro efectivo:
            // si hay remito, los paquetes se retiraron y eso es lo que vale.
            estado: pickups.length
              ? "ejecutada"
              : paradaAnulada(ejecucionIndex, route.id, c)
                ? "anulada"
                : "programada",
            pickups,
            pickup,
            geo: geoPlanned,
            geoCaptured,
          };
        });
        return {
          id: route.id,
          nombre: route.routeName || t("Ruta sin nombre"),
          frecuencia: recurrenceLabel(route),
          // Crudos para precargar el drawer de edición de plantilla.
          frequencyRaw: route.frequency,
          choferId:
            route.deliveryMan?._id ||
            route.deliveryMan?.id ||
            route.idDeliveryMan ||
            route.deliveryManId ||
            (typeof route.deliveryMan === "string" ? route.deliveryMan : ""),
          chofer: route.deliveryManName,
          choferEmail: route.deliveryManEmail,
          // Hub destino + modalidad de la plantilla. El back los devuelve como
          // destinationHubId / deliveryMode en getRoutesWithDetails.
          hubDestinoId:
            route.destinationHubId ?? route.hubDestinoId ?? route.destinationHub?._id ??
            route.destinationHub?.id ?? route.hub?._id ?? route.hub?.id ?? "",
          modalidadEntrega: route.deliveryMode ?? route.modalidadEntrega ?? "hub",
          paradas,
        };
      });
  }, [routes, dow, ejecucionIndex, t]);

  // Índice id → instancia COMPLETA. Lo consume onEditPlantilla para precargar el
  // drawer con TODAS las paradas de la ruta, aunque el listado esté recortado por
  // una búsqueda activa (si no, guardar la ruta perdería las paradas ocultas).
  const instanciasFullById = useMemo(
    () => new Map(instanciasFull.map((i) => [i.id, i])),
    [instanciasFull],
  );

  // Instancias que se MUESTRAN: mismo recorte por búsqueda de siempre, ahora
  // derivado de instanciasFull. El comportamiento visual de la búsqueda no cambia.
  const instancias = useMemo(() => {
    const s = normName(search);
    return instanciasFull
      .filter((inst) => {
        if (!s) return true;
        if (normName(inst.nombre).includes(s) || normName(inst.chofer).includes(s)) return true;
        return inst.paradas.some(
          (p) =>
            normName(p.comercio).includes(s) ||
            normName(p.direccion).includes(s) ||
            normName(p.comuna).includes(s) ||
            String(p.pickup?.correlative || "").includes(s)
        );
      })
      .map((inst) => {
        // si hay búsqueda, recortar paradas a las que calzan
        if (!s) return inst;
        const matchInst = normName(inst.nombre).includes(s) || normName(inst.chofer).includes(s);
        if (matchInst) return inst;
        return {
          ...inst,
          paradas: inst.paradas.filter(
            (p) =>
              normName(p.comercio).includes(s) ||
              normName(p.direccion).includes(s) ||
              normName(p.comuna).includes(s) ||
              String(p.pickup?.correlative || "").includes(s)
          ),
        };
      });
  }, [instanciasFull, search]);

  // Conteos para las KPI cards (sobre el día completo, sin filtro de estado).
  // Derivados de las MISMAS paradas que se listan, así el KPI nunca difiere de las filas.
  //
  // Las recepciones en bodega NO entran acá: no son paradas de ninguna ruta y contarlas
  // como "realizadas" es justamente lo que hacía que una parada que nadie visitó
  // apareciera cumplida. Tienen su propio contador.
  const counts = useMemo(() => {
    let total = 0;
    // Se cuenta por estado real y no como "total - ejecutadas": con ese atajo, cualquier
    // estado nuevo (anulada) se sumaba en silencio a las pendientes.
    const porEstado = { programada: 0, ejecutada: 0, anulada: 0 };
    for (const inst of instanciasFull) {
      for (const p of inst.paradas) {
        total += 1;
        if (p.estado in porEstado) porEstado[p.estado] += 1;
        else porEstado.programada += 1;
      }
    }
    return { total, ...porEstado };
  }, [instanciasFull]);

  const recepciones = ejecucionIndex.recepciones;
  const retirosSinParada = ejecucionIndex.sinParada;

  // Pines del mapa: aplanamos las paradas (con coords) del día seleccionado.
  // Sin filtro de estado (el mapa muestra todo lo del día); el centro se
  // ajusta al promedio de las paradas con coordenadas.
  const mapPins = useMemo(() => {
    const pins = [];
    for (const inst of instancias) {
      // Orden de retiro dentro de la ruta: solo cuenta las paradas con coords
      // (las únicas que se pintan), para que la numeración del pin sea continua.
      let orden = 0;
      const geoParadas = inst.paradas.filter((p) => p.geo);
      for (const p of geoParadas) {
        orden += 1;
        pins.push({
          // Se incluye `orden` porque un cliente puede tener multi-retiro el
          // mismo día: sin él, dos paradas comparten `${inst.id}|${p.id}` y
          // colisiona la key del Marker (React duplica/omite pines).
          id: `${inst.id}|${p.id}|${orden}`,
          lat: p.geo.lat,
          lng: p.geo.lng,
          comercio: p.comercio,
          direccion: p.direccion,
          comuna: p.comuna,
          estado: p.estado,
          rutaId: inst.id,
          ruta: inst.nombre,
          orden,
          paradasRuta: geoParadas.length,
          correlative: p.pickup?.correlative ?? null,
          pdfUrl: p.pickup?.pdfUrl ?? null,
        });
      }
    }
    return pins;
  }, [instancias]);

  // Rutas disponibles para el filtro del mapa (solo las que tienen al menos
  // una parada geocodificada — no tiene sentido filtrar a una ruta sin pins).
  const mapRouteOptions = useMemo(() => {
    const seen = new Map();
    for (const p of mapPins) {
      if (!seen.has(p.rutaId)) seen.set(p.rutaId, p.ruta);
    }
    return [...seen.entries()].map(([id, nombre]) => ({ id, nombre }));
  }, [mapPins]);

  // Pines efectivamente renderizados tras aplicar capas + filtro de ruta.
  const mapPinsVisibles = useMemo(() => {
    return mapPins.filter((p) => {
      if (!mapLayers[p.estado]) return false;
      if (mapRouteId && p.rutaId !== mapRouteId) return false;
      return true;
    });
  }, [mapPins, mapLayers, mapRouteId]);

  // Sitios del mapa: varios retiros pueden caer EXACTAMENTE en el mismo punto
  // (multi-retiro de un comercio, o comercios que comparten dirección). Antes se
  // apilaban pines idénticos y solo el último era visible/hovereable. Redondeo a
  // 5 decimales (~1 m): mismo sitio físico → un solo marcador con conteo.
  // El icono de cluster se construye AQUÍ (memoizado con los sitios) para que su
  // identidad sea estable entre renders y no dispare setIcon en cada tecleo.
  const mapSites = useMemo(() => {
    const by = new Map();
    for (const p of mapPinsVisibles) {
      const key = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
      const site = by.get(key);
      if (site) site.pins.push(p);
      else by.set(key, { key, lat: p.lat, lng: p.lng, pins: [p] });
    }
    const sites = [...by.values()];
    for (const site of sites) {
      if (site.pins.length < 2) continue;
      site.realizadas = site.pins.filter((p) => p.estado === "ejecutada").length;
      site.icon = createSiteClusterIcon({
        count: site.pins.length,
        segments: [
          { color: ESTADO_META.ejecutada.color, n: site.realizadas },
          { color: ESTADO_META.anulada.color, n: site.pins.filter((p) => p.estado === "anulada").length },
          { color: ESTADO_META.programada.color, n: site.pins.filter((p) => p.estado === "programada").length },
        ],
      });
    }
    return sites;
  }, [mapPinsVisibles]);
  const hayClusters = mapSites.some((s) => s.pins.length > 1);

  // Si la ruta filtrada deja de existir (cambio de día), limpiar el filtro.
  useEffect(() => {
    if (mapRouteId && !mapRouteOptions.some((r) => r.id === mapRouteId)) {
      setMapRouteId("");
    }
  }, [mapRouteId, mapRouteOptions]);

  const mapCenter = useMemo(() => {
    if (mapPins.length === 0) return MAP_DEFAULT_CENTER;
    const lat = mapPins.reduce((s, p) => s + p.lat, 0) / mapPins.length;
    const lng = mapPins.reduce((s, p) => s + p.lng, 0) / mapPins.length;
    return [lat, lng];
  }, [mapPins]);

  // Conteo por estado (sobre TODOS los pines del día, sin importar filtros).
  // Lo usamos en los toggles de capas para dar señal de cuántos hay de cada uno.
  const mapEstadoCounts = useMemo(() => {
    const c = { programada: 0, ejecutada: 0, anulada: 0 };
    for (const p of mapPins) if (p.estado in c) c[p.estado] += 1;
    return c;
  }, [mapPins]);

  // Cronología real del día, por hora. Incluye TODO lo que ocurrió, con su tipo:
  // retiros de ruta, recepciones en bodega y los retiros que llegaron sin parada.
  //
  // El comercio y la dirección salen de la plantilla (`instanciasFull`), no del retiro:
  // el endpoint de ejecución devuelve ids, no textos. Es un lookup por id contra datos
  // que la vista ya tiene, no un cruce por nombre.
  const cronologia = useMemo(() => {
    const eventos = [];

    for (const inst of instanciasFull) {
      for (const parada of inst.paradas) {
        for (const pk of parada.pickups) {
          eventos.push({
            id: pk._id,
            tipo: "retiro",
            correlative: pk.correlative,
            date: pk.date,
            pdfUrl: pk.pdfUrl,
            comercio: parada.comercio,
            detalle: [parada.direccion, parada.comuna].filter(Boolean).join(" · "),
            persona: pk.registradoPor?.nombre || inst.chofer || "",
          });
        }
      }
    }

    for (const r of recepciones) {
      eventos.push({
        id: r.pickupId,
        tipo: "recepcion",
        correlative: r.correlative,
        date: r.fecha,
        pdfUrl: r.pdfUrl,
        comercio: r.comercioNombre,
        // Una recepción no tiene dirección de retiro: el comercio vino a la bodega.
        detalle: r.entregadoPor?.fullName ? `${t("Entregó")}: ${r.entregadoPor.fullName}` : "",
        persona: r.registradoPor?.nombre || "",
      });
    }

    for (const s of retirosSinParada) {
      eventos.push({
        id: s._id,
        tipo: "sin_parada",
        correlative: s.correlative,
        date: s.date,
        pdfUrl: s.pdfUrl,
        comercio: "",
        detalle: t("El comercio no figuraba entre las paradas de la ruta"),
        persona: s.registradoPor?.nombre || "",
      });
    }

    // De más reciente a más viejo: lo último que pasó queda arriba de la
    // cronología (los grupos por hora heredan este orden de inserción).
    eventos.sort((a, b) => new Date(b.date) - new Date(a.date));

    const groups = {};
    for (const e of eventos) {
      const hora = `${horaDe(e.date).slice(0, 2)}:00`;
      (groups[hora] ??= []).push(e);
    }
    return Object.entries(groups);
  }, [instanciasFull, recepciones, retirosSinParada, t]);

  // ── filtro por estado aplicado a las paradas mostradas ──
  const instanciasFiltradas = useMemo(() => {
    if (!estado) return instancias;
    return instancias
      .map((inst) => ({ ...inst, paradas: inst.paradas.filter((p) => p.estado === estado) }))
      .filter((inst) => inst.paradas.length > 0);
  }, [instancias, estado]);

  // Ventana visible del listado (scroll infinito client-side).
  const visibleInstancias = useMemo(
    () => instanciasFiltradas.slice(0, visibleCount),
    [instanciasFiltradas, visibleCount],
  );
  const hasMoreInstancias = visibleCount < instanciasFiltradas.length;

  // Reinicia la paginación al cambiar día / búsqueda / filtro de estado.
  useEffect(() => {
    setVisibleCount(ROUTES_PAGE_SIZE);
  }, [selectedDate, search, estado]);

  // Centinela: carga la siguiente página cuando se acerca al fondo del viewport.
  // NO usamos IntersectionObserver: su `root: null` (viewport) no detecta de forma
  // fiable el scroll cuando ocurre en un contenedor anidado (en desktop el scroller
  // es `.layout-main`; en mobile es otro). Eso dejaba el listado pegado en la
  // primera página aunque el centinela estuviera visible en pantalla. En su lugar
  // escuchamos `scroll` en FASE DE CAPTURA (capta el scroll de CUALQUIER contenedor)
  // y medimos la posición real del centinela con getBoundingClientRect.
  useEffect(() => {
    if (!hasMoreInstancias) return undefined;
    const check = () => {
      const node = sentinelRef.current;
      if (!node) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // top del centinela dentro de 320px del fondo visible → cargar más.
      if (node.getBoundingClientRect().top <= vh + 320) {
        setVisibleCount((c) => Math.min(c + ROUTES_PAGE_SIZE, instanciasFiltradas.length));
      }
    };
    check(); // chequeo inmediato: rellena el viewport (cascada) si ya está a la vista
    // capture:true → recibe los eventos de scroll de contenedores internos anidados.
    window.addEventListener("scroll", check, true);
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check, true);
      window.removeEventListener("resize", check);
    };
  }, [hasMoreInstancias, instanciasFiltradas.length, visibleCount]);

  // ── handlers ──
  // Lleva a "Colectas sin ruta" y deja el foco ahí. Mover el foco no es un extra: sin
  // eso, quien navega con teclado o lector de pantalla no va a ninguna parte — el scroll
  // solo mueve píxeles. `scroll-margin-top` (SCSS) evita que la cabecera tape el título.
  const irAColectasSinRuta = () => {
    const node = colectasRef.current;
    if (!node) return;
    const sinMovimiento = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    node.scrollIntoView({ behavior: sinMovimiento ? "auto" : "smooth", block: "start" });
    node.focus({ preventScroll: true });
  };

  const navDay = (delta) => {
    const d = parseYmd(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(ymd(d));
  };
  // La tira de 7 días esconde ~2 días en teléfono. El degradado del borde
  // derecho (CSS) es la señal de "hay más"; acá solo lo apagamos al llegar al
  // final. Se toca la clase directo sobre el nodo: es scroll, no estado de React.
  const syncFranjaEnd = (el) => {
    if (!el) return;
    const scrollable = el.scrollWidth - el.clientWidth > 8;
    const atEnd = !scrollable || el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    el.classList.toggle("is-scroll-end", atEnd);
  };
  const handleFranjaScroll = (e) => syncFranjaEnd(e.currentTarget);
  useEffect(() => {
    const el = franjaRef.current;
    if (!el) return undefined;
    // rAF: en el tick del efecto el nodo puede no estar maquetado todavía
    // (clientWidth 0) y quedaría marcado como "final" para siempre.
    const raf = requestAnimationFrame(() => syncFranjaEnd(el));
    const onResize = () => syncFranjaEnd(el);
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [isMobile, selectedDate]);
  const toggleGroup = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clearAll = () => {
    setSearch("");
    setEstado("");
  };

  const isToday = selectedDate === todayYmd;
  // Retiros de ruta del día. Las recepciones van en su propio contador: sumarlas acá
  // volvería a mezclar dos operaciones distintas bajo un solo número.
  const totalRetirosHoy = counts.ejecutada;

  // Paradas de rutas sin chofer: badge de alerta en el tab Monitoreo.
  const sinAsignarCount = useMemo(
    () => instanciasFull.reduce((s, i) => (i.chofer ? s : s + i.paradas.length), 0),
    [instanciasFull],
  );

  // Subtítulo del header según pestaña (mismo patrón que el mockup mu-portal).
  const subtituloPorTab = {
    monitoreo: t("Torre de seguimiento del día: avance real, rutas en calle y carga por hora y comuna."),
    retiros: t("Detalle parada a parada del día: filtros por estado, búsqueda y descarga CSV."),
    planificacion: t(
      "Rutas recurrentes de retiro (first-mile). Cada día genera las paradas asignables al chofer del turno.",
    ),
  };

  // Vista vieja "Administrar retiros": el switcher arriba + el componente legacy
  // reusado tal cual. Todos los hooks ya corrieron arriba, así que este
  // early-return no rompe las reglas de hooks.
  if (SHOW_VISTA_VIEJA && vista === "vieja") {
    return (
      <div className="rec-legacy-host">
        <div className="rec-legacy-host__bar">
          <ViewSwitch vista={vista} setVista={setVista} t={t} />
        </div>
        <div role="tabpanel" id="rec-panel-vieja" aria-labelledby="rec-tab-vieja">
          <AdminWithdrawalsView />
        </div>
      </div>
    );
  }

  return (
    <div className="rec">
      {pdfModal}
      {SHOW_VISTA_VIEJA && <ViewSwitch vista={vista} setVista={setVista} t={t} />}
      {/* ── Header ── */}
      <header className="rec__header">
        <div>
          <h1 className="rec__title">{t("Recolecciones")}</h1>
          <p className="rec__subtitle">{subtituloPorTab[tab] ?? subtituloPorTab.planificacion}</p>
        </div>
        <div className="rec__header-actions">
          {/* Atajo a /admin/inform — el botón ya no exporta a Excel desde acá,
              redirige al creador de informes que centraliza export Excel/PDF. */}
          <button
            className="rec-btn rec-btn--secondary"
            onClick={() => navigate("/admin/inform")}
            title={t("Ir al creador de informes")}
          >
            <ExternalLink size={16} />
            {/* Decía "Descargar informe" pero navega al creador de informes: quien
                buscaba la descarga volvía con el botón atrás (quickback en Clarity). */}
            {t("Ir a informes")}
          </button>
          <button
            className="rec-btn rec-btn--primary"
            onClick={() => {
              setEditandoRuta(null);
              setProgramarOpen(true);
            }}
            title={t("Crear o programar una ruta de recolección")}
          >
            <Plus size={16} />
            {t("Programar ruta")}
          </button>
        </div>
      </header>

      {/* ── Pestañas (port de los menús del mockup mu-portal) ── */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList ariaLabel={t("Secciones de recolecciones")}>
          {/* Bajo `md` (el mismo corte en que el TabsList deja de ser scrolleable, ver
              ui/Tabs) los tres rótulos con su icono miden ~408px y la fila dispone de
              368: la última pestaña quedaba cortada a mitad de palabra. Sin el icono
              —decorativo, `aria-hidden`— entran las tres sin scroll. */}
          <TabsTrigger value="planificacion" icon={hideTabIcons ? undefined : <CalendarClock />}>
            {t("Planificación")}
            <Badge tone="neutral">{counts.total}</Badge>
          </TabsTrigger>
          <TabsTrigger value="retiros" icon={hideTabIcons ? undefined : <Package />}>
            {t("Retiros")}
          </TabsTrigger>
          <TabsTrigger value="monitoreo" icon={hideTabIcons ? undefined : <Radar />}>
            {t("Monitoreo")}
            {sinAsignarCount > 0 && <Badge tone="error">{sinAsignarCount}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ============ Monitoreo ============ */}
        <TabsContent value="monitoreo" className="pt-1">
          <MonitorTab
            loading={loading}
            instancias={instanciasFull}
            recepciones={recepciones}
            sinParada={retirosSinParada}
            selectedDate={selectedDate}
            onSelectedDateChange={setSelectedDate}
            todayYmd={todayYmd}
            onVerRuta={(r) => {
              // Drill-down real: abre Planificación ya filtrada a esa ruta.
              setSearch(r.nombre);
              setEstado("");
              setTab("planificacion");
            }}
          />
        </TabsContent>

        {/* ============ Retiros ============ */}
        <TabsContent value="retiros" className="pt-1">
          <RetirosTab
            loading={loading}
            instancias={instanciasFull}
            selectedDate={selectedDate}
            onSelectedDateChange={setSelectedDate}
            todayYmd={todayYmd}
          />
        </TabsContent>

        {/* ============ Planificación (vista original) ============ */}
        <TabsContent value="planificacion" className="pt-1">
          <div className="space-y-5">
      {/* ── Navegador de día + franja ── */}
      <div className="rec-card rec-card--flush">
        {isMobile ? (
          // En teléfono la misma fecha se repetía cuatro veces (título, badge
          // "Hoy", botón "Hoy" e input) y se comía 152px del primer fold. Acá va
          // una sola fila de 44px: flechas, la fecha (que ES el date picker) y
          // "Hoy" solo cuando no estás en hoy. La tira de abajo sigue siendo el
          // selector principal.
          <div className="rec-daynav rec-daynav--mobile">
            <button
              type="button"
              className="rec-daynav__arrow"
              onClick={() => navDay(-1)}
              aria-label={t("Día anterior")}
            >
              <ChevronLeft size={18} />
            </button>
            <label className="rec-daynav__pick">
              <Calendar size={15} className="rec-ico-brand" aria-hidden />
              <span className="rec-daynav__label">{formatDiaCorto(selectedDate)}</span>
              <input
                type="date"
                className="rec-daynav__pickinput"
                aria-label={t("Elegir fecha")}
                value={selectedDate}
                onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="rec-daynav__arrow"
              onClick={() => navDay(1)}
              aria-label={t("Día siguiente")}
            >
              <ChevronRight size={18} />
            </button>
            {/* Señal POSITIVA de que estás en hoy: la ausencia del botón "Hoy"
                no basta como pista. Badge y botón se excluyen, así que la fila
                no crece. */}
            {isToday ? (
              <span className="rec-badge rec-badge--brand">{t("Hoy")}</span>
            ) : (
              <button
                type="button"
                className="rec-btn rec-btn--outline rec-btn--sm"
                onClick={() => setSelectedDate(todayYmd)}
              >
                {t("Hoy")}
              </button>
            )}
          </div>
        ) : (
          <div className="rec-daynav">
            <div className="rec-daynav__current">
              <Calendar size={16} className="rec-ico-brand" />
              <span className="rec-daynav__label">{formatLargo(selectedDate)}</span>
              {isToday && <span className="rec-badge rec-badge--brand">{t("Hoy")}</span>}
            </div>
            <div className="rec-daynav__controls">
              <button className="rec-btn rec-btn--ghost rec-btn--sm" onClick={() => navDay(-1)}>
                <ChevronLeft size={16} />
                {t("Día anterior")}
              </button>
              <button
                className={`rec-btn rec-btn--sm ${isToday ? "rec-btn--outline" : "rec-btn--ghost"}`}
                onClick={() => setSelectedDate(todayYmd)}
              >
                {t("Hoy")}
              </button>
              <button className="rec-btn rec-btn--ghost rec-btn--sm" onClick={() => navDay(1)}>
                {t("Día siguiente")}
                <ChevronRight size={16} />
              </button>
              <input
                type="date"
                className="rec-dateinput"
                value={selectedDate}
                onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
              />
            </div>
          </div>
        )}
        <div className="rec-franja" ref={franjaRef} onScroll={handleFranjaScroll}>
          {fechasFranja.map((f) => {
            const r = resumenPorFecha.get(f);
            const d = parseYmd(f);
            const isSel = f === selectedDate;
            const isFToday = f === todayYmd;
            const isFuture = f > todayYmd;
            const conds = r ? Array.from(r.conductores) : [];
            return (
              <button
                key={f}
                className={`rec-franja__day ${isSel ? "is-selected" : ""}`}
                onClick={() => setSelectedDate(f)}
              >
                <div className="rec-franja__top">
                  <span className={`rec-franja__dow ${isFToday ? "is-today" : ""}`}>{t(DOW_SHORT[d.getDay()])}</span>
                  <span className={`rec-franja__num ${isFToday ? "is-today" : ""}`}>{d.getDate()}</span>
                  <span className="rec-franja__mon">{t(MONTH_SHORT[d.getMonth()])}</span>
                </div>
                {r && r.rutas > 0 ? (
                  <div className="rec-franja__body">
                    <div className="rec-franja__stat">
                      <RouteIcon size={12} />
                      <strong>{r.rutas}</strong> {r.rutas !== 1 ? t("rutas") : t("ruta")}
                    </div>
                    <div className="rec-franja__stat rec-franja__stat--muted">
                      <Package size={12} />
                      <strong>{r.retiros}</strong> {r.retiros !== 1 ? t("retiros") : t("retiro")}
                    </div>
                    {conds.length > 0 && (
                      <div className="rec-avatars">
                        {conds.slice(0, 3).map((n) => (
                          <span key={n} className="rec-avatar rec-avatar--xs" title={n}>
                            {iniciales(n)}
                          </span>
                        ))}
                        {conds.length > 3 && <span className="rec-avatar rec-avatar--more">+{conds.length - 3}</span>}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rec-franja__empty">{isFuture ? t("sin programar") : t("sin actividad")}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Mapa del día (placeholder por falta de coords) + cronología real ── */}
      <div className="rec-card rec-card--flush">
        <div className="rec-mapcard__head">
          <div className="rec-mapcard__title">
            <MapPin size={16} className="rec-ico-brand" />
            <h2>{t("Mapa del día")} — {formatLargo(selectedDate)}</h2>
            <span className="rec-badge rec-badge--brand">{t("{{n}} retiros", { n: totalRetirosHoy })}</span>
            {/* Atajo, no adorno: el contador es el único indicio de que abajo hay
                recepciones, así que se comporta como botón y baja hasta ellas. */}
            {(recepciones.length > 0 || retirosSinParada.length > 0) && (
              <button
                type="button"
                className="rec-jump"
                onClick={irAColectasSinRuta}
                aria-controls="colectas-sin-ruta"
              >
                <Warehouse size={12} aria-hidden="true" />
                {recepciones.length > 0
                  ? t("{{n}} recepciones en bodega", { n: recepciones.length })
                  : t("{{n}} sin parada identificada", { n: retirosSinParada.length })}
                <ChevronDown size={12} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        <div className="rec-mapcard__body">
          <div className="rec-mapcard__map">
            {mapPins.length === 0 ? (
              <div className="rec-map-placeholder">
                <MapPin size={32} />
                <p className="rec-map-placeholder__title">{t("Sin coordenadas para este día")}</p>
                <p className="rec-map-placeholder__desc">
                  {t("Las paradas del día no tienen coordenadas geocodificadas, o no hay rutas programadas.")}
                </p>
              </div>
            ) : (
              <div className="rec-mapwrap">
                <MapContainer
                  key={selectedDate}
                  center={mapCenter}
                  zoom={MAP_DEFAULT_ZOOM}
                  scrollWheelZoom={false}
                  zoomControl
                  className="rec-leaflet"
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    key={tile.url}
                    attribution={tile.attribution}
                    url={tile.url}
                    subdomains={tile.subdomains}
                    maxZoom={tile.maxZoom}
                  />
                  <FitToPins pins={mapPinsVisibles} />
                  {/* El crecimiento en hover es CSS puro (.rec-pin:hover):
                      regenerar el icono por estado React reemplazaba el DOM
                      bajo el cursor, Leaflet perdía el mouseout y los tooltips
                      quedaban pegados al pasar rápido por varios pines. */}
                  {mapSites.filter((s) => s.pins.length === 1).map(({ pins: [p] }) => {
                    const color = estadoColor(p.estado);
                    return (
                      <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.estado)}>
                        <Tooltip direction="top" offset={[0, -10]}>
                          <div className="rec-maptip">
                            <strong>{p.comercio}</strong>
                            <span className="rec-maptip__estado">
                              <span className="rec-maptip__dot" style={{ background: color }} aria-hidden />
                              {t((ESTADO_META[p.estado] ?? ESTADO_META.programada).label)}
                            </span>
                          </div>
                        </Tooltip>
                        <Popup>
                          <div className="rec-pinpop">
                            <div className="rec-pinpop__head">
                              <span
                                className="rec-pinpop__dot"
                                style={{ background: color }}
                                aria-hidden
                              />
                              <div className="rec-pinpop__title">
                                <div className="rec-pinpop__comercio">{p.comercio}</div>
                              </div>
                            </div>
                            <div className="rec-pinpop__addr">
                              {p.direccion}
                              {p.comuna ? ` · ${p.comuna}` : ""}
                            </div>
                            <dl className="rec-pinpop__meta">
                              <div className="rec-pinpop__row">
                                <dt>{t("Estado")}</dt>
                                <dd>
                                  <span
                                    className="rec-pinpop__status"
                                    style={{ color, borderColor: color }}
                                  >
                                    {t((ESTADO_META[p.estado] ?? ESTADO_META.programada).label)}
                                  </span>
                                </dd>
                              </div>
                              <div className="rec-pinpop__row">
                                <dt>{t("Ruta")}</dt>
                                <dd>
                                  <button
                                    type="button"
                                    className="rec-popup-link"
                                    onClick={() => setMapRouteId(p.rutaId)}
                                    title={t("Filtrar el mapa a esta ruta")}
                                  >
                                    {p.ruta}
                                  </button>
                                </dd>
                              </div>
                              {p.correlative != null && (
                                <div className="rec-pinpop__row">
                                  <dt>{t("Pickup")}</dt>
                                  <dd><span className="rec-mono">#{p.correlative}</span></dd>
                                </div>
                              )}
                            </dl>
                            {p.pdfUrl && (
                              pdfLink({
                                url: p.pdfUrl,
                                correlative: p.correlative,
                                className: "rec-link rec-pinpop__pdf",
                                children: (
                                  <>
                                    <FileText size={12} /> {t("Ver PDF")}
                                  </>
                                ),
                              })
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                  {mapSites.filter((s) => s.pins.length > 1).map((site) => (
                    <Marker key={site.key} position={[site.lat, site.lng]} icon={site.icon}>
                      <Tooltip direction="top" offset={[0, -16]}>
                        <div className="rec-maptip">
                          <strong>{t("{{n}} retiros en este punto", { n: site.pins.length })}</strong>
                          <span className="rec-maptip__estado">
                            {t("{{x}} de {{n}} realizados", { x: site.realizadas, n: site.pins.length })}
                          </span>
                        </div>
                      </Tooltip>
                      <Popup>
                        <div className="rec-pinpop rec-pinpop--cluster">
                          <div className="rec-pinpop__head">
                            <span className="rec-pinpop__clusterdot" aria-hidden>{site.pins.length}</span>
                            <div className="rec-pinpop__title">
                              <div className="rec-pinpop__comercio">
                                {t("{{n}} retiros en este punto", { n: site.pins.length })}
                              </div>
                            </div>
                          </div>
                          <div className="rec-pinpop__addr">
                            {site.pins[0].direccion}
                            {site.pins[0].comuna ? ` · ${site.pins[0].comuna}` : ""}
                          </div>
                          <div className="rec-pinpop__progress">
                            {t("{{x}} de {{n}} realizados", { x: site.realizadas, n: site.pins.length })}
                          </div>
                          <ul className="rec-pinpop__list">
                            {site.pins.map((p) => (
                              <li key={p.id} className="rec-pinpop__item">
                                <span className="rec-pinpop__itemstate" data-estado={p.estado} aria-hidden>
                                  {p.estado === "ejecutada" && <Check size={9} strokeWidth={4.5} />}
                                </span>
                                <div className="rec-pinpop__itemmain">
                                  <div className="rec-pinpop__itemname">{p.comercio}</div>
                                  <div className="rec-pinpop__itemsub">
                                    {p.correlative != null ? (
                                      <span className="rec-mono">#{p.correlative}</span>
                                    ) : (
                                      <span>{t((ESTADO_META[p.estado] ?? ESTADO_META.programada).label)}</span>
                                    )}
                                    <span className="rec-pinpop__itemruta">{p.ruta}</span>
                                  </div>
                                </div>
                                {p.pdfUrl && (
                                  pdfLink({
                                    url: p.pdfUrl,
                                    correlative: p.correlative,
                                    className: "rec-link",
                                    title: t("Ver PDF"),
                                    children: <FileText size={12} />,
                                  })
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>

                {/* Panel flotante: Capas + filtro por ruta. Top-right del mapa. */}
                <MapControlsPanel
                  t={t}
                  open={mapPanelOpen}
                  onToggle={() => setMapPanelOpen((v) => !v)}
                  layers={mapLayers}
                  onLayerToggle={(estadoKey) =>
                    setMapLayers((prev) => ({ ...prev, [estadoKey]: !prev[estadoKey] }))
                  }
                  counts={mapEstadoCounts}
                  routeOptions={mapRouteOptions}
                  routeId={mapRouteId}
                  onRouteChange={setMapRouteId}
                  visibleCount={mapPinsVisibles.length}
                  totalCount={mapPins.length}
                />

                {/* Leyenda SIEMPRE visible: antes el significado de los colores
                    vivía escondido tras el panel "Capas" y el semáforo no se
                    entendía. Los chips además togglean su capa (mismo estado
                    que el panel). El swatch replica la forma real del pin
                    (aro hueco vs disco con ✓), no solo su color. */}
                <div className="rec-maplegend" role="group" aria-label={t("Leyenda del mapa")}>
                  <button
                    type="button"
                    className={`rec-maplegend__chip ${mapLayers.programada ? "" : "is-off"}`}
                    aria-pressed={mapLayers.programada}
                    onClick={() => setMapLayers((prev) => ({ ...prev, programada: !prev.programada }))}
                    title={t("Mostrar u ocultar en el mapa")}
                  >
                    <span className="rec-maplegend__pin rec-maplegend__pin--pendiente" aria-hidden />
                    {t("Pendientes")} <strong>{mapEstadoCounts.programada}</strong>
                  </button>
                  <button
                    type="button"
                    className={`rec-maplegend__chip ${mapLayers.ejecutada ? "" : "is-off"}`}
                    aria-pressed={mapLayers.ejecutada}
                    onClick={() => setMapLayers((prev) => ({ ...prev, ejecutada: !prev.ejecutada }))}
                    title={t("Mostrar u ocultar en el mapa")}
                  >
                    <span className="rec-maplegend__pin rec-maplegend__pin--realizada" aria-hidden>
                      <Check size={8} strokeWidth={4.5} />
                    </span>
                    {t("Realizadas")} <strong>{mapEstadoCounts.ejecutada}</strong>
                  </button>
                  {hayClusters && (
                    <span className="rec-maplegend__chip rec-maplegend__chip--info">
                      <span className="rec-maplegend__pin rec-maplegend__pin--cluster" aria-hidden />
                      {t("nº = varios retiros en el mismo punto")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="rec-mapcard__timeline">
            <div className="rec-timeline__head">
              <div className="rec-timeline__title">{t("Retiros realizados hoy")}</div>
              <div className="rec-timeline__sub">{t("por hora · datos reales de pickups")}</div>
            </div>
            {cronologia.length === 0 ? (
              <div className="rec-timeline__empty">{t("Sin retiros registrados este día.")}</div>
            ) : (
              <ul className="rec-timeline__list">
                {cronologia.map(([hora, items]) => (
                  <li key={hora}>
                    <div className="rec-timeline__hour">{hora}</div>
                    <ul>
                      {items.map((e) => (
                        <li key={e.id} className="rec-timeline__item">
                          <span className={`rec-dot rec-dot--${TIPO_EVENTO[e.tipo].dot}`} />
                          <div className="rec-timeline__item-body">
                            <div className="rec-timeline__item-top">
                              <span className="rec-mono">#{e.correlative}</span>
                              <span className={`rec-badge rec-badge--${TIPO_EVENTO[e.tipo].tone}`}>
                                {t(TIPO_EVENTO[e.tipo].label)}
                              </span>
                              <span className="rec-timeline__time">{horaDe(e.date)}</span>
                            </div>
                            {e.comercio && <div className="rec-timeline__comercio">{e.comercio}</div>}
                            {e.detalle && <div className="rec-timeline__addr">{e.detalle}</div>}
                            <div className="rec-timeline__meta">
                              {e.persona && (
                                <span className="rec-timeline__driver">
                                  <span className="rec-avatar rec-avatar--xs">{iniciales(e.persona)}</span>
                                  {e.persona}
                                </span>
                              )}
                              {e.pdfUrl && (
                                pdfLink({
                                  url: e.pdfUrl,
                                  correlative: e.correlative,
                                  className: "rec-link",
                                  children: (
                                    <>
                                      <FileText size={12} /> PDF
                                    </>
                                  ),
                                })
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI filter cards ── */}
      <div className="rec-kpis">
        {ESTADO_CARDS.map((c) => {
          const Icon = c.icon;
          const isActive = estado === c.value;
          const count = c.value === "" ? counts.total : counts[c.value] ?? 0;
          const pct = counts.total === 0 ? 0 : (count / counts.total) * 100;
          return (
            <button
              key={c.value || "all"}
              className={`rec-kpi rec-kpi--${c.tone} ${isActive ? "is-active" : ""}`}
              onClick={() => setEstado(c.value)}
            >
              <div className="rec-kpi__top">
                <span className="rec-kpi__icon">
                  <Icon size={16} />
                </span>
                <span className="rec-kpi__count">{formatNumberCL(count)}</span>
              </div>
              <div className="rec-kpi__label">
                {t(c.label)}
                {/* "·pend." era jerga: en táctil el `title` no se dispara nunca,
                    así que el usuario veía una abreviatura sin contexto. */}
                {c.pending && <span className="rec-kpi__pending">{t("En construcción")}</span>}
              </div>
              {c.value !== "" && (
                <div className="rec-kpi__bar">
                  {/* scaleX en vez de width: anima en compositor, sin relayout. */}
                  <div className="rec-kpi__bar-fill" style={{ transform: `scaleX(${pct / 100})` }} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Búsqueda ── */}
      <div className="rec-searchrow">
        <div className="rec-search">
          <Search size={16} className="rec-search__icon" />
          <input
            type="search"
            placeholder={t("Buscar por ruta, comercio, dirección o correlativo…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {(search || estado) && (
          <button className="rec-btn rec-btn--ghost" onClick={clearAll}>
            <X size={16} />
            {t("Limpiar")}
          </button>
        )}
      </div>

      {/* ── Grupos de instancias ── */}
      {loading ? (
        <div className="rec-card rec-skel">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rec-skel__row" />
          ))}
        </div>
      ) : instanciasFiltradas.length === 0 ? (
        <div className="rec-card rec-empty">
          <Search size={28} />
          <p className="rec-empty__title">{t("Sin rutas para este día")}</p>
          <p className="rec-empty__desc">
            {counts.total === 0
              ? t("Ninguna ruta de retiro está programada para esta fecha (según su frecuencia).")
              : t("No hay paradas que calcen con los filtros activos.")}
          </p>
        </div>
      ) : (
        <>
        <div className="rec-groups">
          {visibleInstancias.map((inst) => (
            <InstanciaGrupo
              key={inst.id}
              inst={inst}
              pdfLink={pdfLink}
              isCollapsed={collapsed.has(inst.id)}
              onToggle={() => toggleGroup(inst.id)}
              onEditPlantilla={(i) => {
                // Abre el drawer "Programar ruta" en modo edición, precargado
                // con la ruta real (nombre, frecuencia, chofer y paradas con sus
                // IDs de cliente/dirección para poder guardar contra el back).
                setEditandoRuta({
                  id: i.id,
                  nombre: i.nombre,
                  hubDestinoId: i.hubDestinoId || "",
                  modalidadEntrega: i.modalidadEntrega || "hub",
                  conductorPorDefectoId: i.choferId || "",
                  recurrencia: frequencyToRecurrencia(i.frequencyRaw),
                  // Precargar SIEMPRE el conjunto COMPLETO de paradas de la ruta.
                  // `i` viene del listado, que puede estar recortado por una
                  // búsqueda activa; instanciasFullById guarda la instancia sin
                  // recorte. Fallback a `i.paradas` por si la instancia no está
                  // en el índice (defensivo).
                  paradas: (instanciasFullById.get(i.id)?.paradas ?? i.paradas).map((p) => ({
                    id: p.clientId || p.id,
                    clientId: p.clientId || null,
                    selectedAddress: p.selectedAddress || "",
                    comercioNombre: p.comercio,
                    direccion: p.direccion,
                    comuna: p.comuna,
                    // Datos operativos ya guardados → el drawer los precarga.
                    ventanaDesde: p.ventanaDesde,
                    ventanaHasta: p.ventanaHasta,
                    bultosEsperados: p.bultosEsperados,
                    pedidosEsperados: p.pedidosEsperados,
                    pesoEsperadoKg: p.pesoEsperadoKg,
                    valorCobrarCliente: p.valorCobrarCliente,
                    valorPagarRepartidor: p.valorPagarRepartidor,
                    umbralCobroPedidos: p.umbralCobroPedidos,
                    geofencingActivo: p.geofencingActivo,
                    geofencingMetros: p.geofencingMetros,
                  })),
                });
                setProgramarOpen(true);
              }}
              onDeletePlantilla={(i) => setDeleteTarget(i)}
            />
          ))}
        </div>
        {hasMoreInstancias && (
          <div
            ref={sentinelRef}
            className="flex items-center justify-center gap-2 py-6 text-sm text-fg-muted"
            role="status"
            aria-live="polite"
          >
            <span
              className="inline-block w-5 h-5 border-2 border-border border-t-brand-500 rounded-full animate-spin"
              aria-hidden="true"
            />
            {t("Cargando más rutas...")}
          </div>
        )}
        <p className="text-center text-xs text-fg-muted pb-2">
          {t("Mostrando {{v}} de {{n}} rutas", { v: visibleInstancias.length, n: instanciasFiltradas.length })}
        </p>

        <ColectasSinRuta
          sectionRef={colectasRef}
          recepciones={recepciones}
          sinParada={retirosSinParada}
        />
        </>
      )}
          </div>
        </TabsContent>
      </Tabs>

      <ProgramarRecoleccionModal
        open={programarOpen}
        onClose={() => {
          setProgramarOpen(false);
          setEditandoRuta(null);
        }}
        editandoRuta={editandoRuta}
        drivers={deliveries}
        clients={clients}
        clientsLoading={clientsLoading}
        hubs={hubsFromCompany}
        onSaved={() => {
          // Refrescar el listado tras crear/editar
          setLoading(true);
          fetchRecoleccionesContext({ fechas: fechasFranja }).then(({ routes: r, ejecucionPorFecha }) => {
            setRoutes(r);
            setEjecuciones(ejecucionPorFecha);
            setLoading(false);
          });
        }}
      />

      {/* Confirmar eliminación de ruta (plantilla). DELETE /routes { id }. */}
      <Modal
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        size="sm"
        title={t("Eliminar ruta")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("Cancelar")}
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              onClick={async () => {
                if (!deleteTarget) return;
                setDeleting(true);
                try {
                  const res = await deleteRoutesApi(deleteTarget.id);
                  if (res?.error) {
                    toast.error(res.error?.message || t("No se pudo eliminar la ruta."));
                  } else {
                    toast.success(t("Ruta eliminada."));
                    setDeleteTarget(null);
                    setLoading(true);
                    const { routes: r, ejecucionPorFecha } = await fetchRecoleccionesContext({
                      fechas: fechasFranja,
                    });
                    setRoutes(r);
                    setEjecuciones(ejecucionPorFecha);
                    setLoading(false);
                  }
                } catch (e) {
                  toast.error(t("No se pudo eliminar la ruta."));
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {t("Eliminar ruta")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">
          {t("¿Seguro que deseas eliminar la ruta")}{" "}
          <strong className="font-semibold text-fg">{deleteTarget?.nombre}</strong>?{" "}
          {t("Se eliminará la plantilla y sus paradas. Esta acción no se puede deshacer.")}
        </p>
      </Modal>
    </div>
  );
}

// ───────────────────────── celda "Retiro" ─────────────────────────
// Un cliente puede tener VARIOS retiros el mismo día. Hasta RETIRO_INLINE_MAX se
// muestran como pills compactos; con más, se colapsa a un resumen "{n} retiros"
// expandible, para que la fila no se dispare en alto (antes un cliente con 11
// retiros generaba una fila de ~280px, descuadrada del resto de la tabla).
const RETIRO_INLINE_MAX = 2;

// `pdfLink` llega del padre: el visor de PDF es uno por tabla, no uno por fila.
export function RetiroCell({ pickups, pdfLink }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!pickups?.length) return <span className="rec-cell-muted">—</span>;
  const many = pickups.length > RETIRO_INLINE_MAX;
  const showList = !many || expanded;
  if (!showList) {
    return (
      <div className="rec-retiro-list" role="list" data-count={pickups.length}>
        <button
          type="button"
          className="rec-retiro__summary"
          onClick={() => setExpanded(true)}
          title={t("Ver los retiros")}
        >
          <Package size={12} />
          {t("{{n}} retiros", { n: pickups.length })}
          <ChevronDown size={12} />
        </button>
      </div>
    );
  }
  return (
    <div className="rec-retiro-list" role="list" data-count={pickups.length}>
      {pickups.map((pk, i) => (
        <span className="rec-retiro" role="listitem" key={pk._id || pk.correlative || i}>
          <span className="rec-mono">#{pk.correlative}</span>
          <CopyButton
            value={pk.correlative}
            label={t("Copiar número de seguimiento")}
            copiedMessage={t("Número de seguimiento copiado")}
          />
          {pk.date && (
            <span className="rec-retiro__time">
              <Clock size={11} /> {horaDe(pk.date)}
            </span>
          )}
          {pk.pdfUrl && (
            pdfLink({
              url: pk.pdfUrl,
              correlative: pk.correlative,
              className: "rec-retiro__pdf",
              title: t("Ver PDF"),
              children: <FileText size={12} />,
            })
          )}
        </span>
      ))}
      {many && (
        <button type="button" className="rec-retiro__more" onClick={() => setExpanded(false)}>
          {t("Ver menos")}
        </button>
      )}
    </div>
  );
}

// ───────────────────────── grupo (instancia del día) ─────────────────────────

// ───────────────── colectas que no pertenecen a ninguna ruta ─────────────────

/**
 * Bloque hermano de las rutas, para lo que ocurrió el día pero NO es una parada:
 *
 *  • RECEPCIONES EN BODEGA: el comercio llevó los paquetes y un supervisor los recibió.
 *    No hay ruta, ni repartidor, ni ventana, ni monto a cobrar por retiro. Antes esta
 *    pantalla las colgaba de la parada de alguna ruta del mismo comercio y la mostraba
 *    "Realizada", inventando un retiro que nadie hizo.
 *  • RETIROS SIN PARADA: traen ruta pero no parada, porque al emitirse el remito el
 *    comercio no figuraba entre las paradas de esa ruta. No se los atribuye a una parada
 *    al azar; se muestran acá para que no queden invisibles.
 */
function ColectasSinRuta({ recepciones, sinParada, sectionRef }) {
  const { t } = useTranslation();
  const { pdfLink, pdfModal } = usePdfViewer();
  if (!recepciones.length && !sinParada.length) return null;

  return (
    // `tabIndex={-1}` para poder recibir el foco desde el atajo de la cabecera sin
    // entrar en el orden natural de tabulación.
    <section
      id="colectas-sin-ruta"
      ref={sectionRef}
      tabIndex={-1}
      aria-labelledby="colectas-sin-ruta-titulo"
      className="rec-card rec-card--flush rec-sinruta"
    >
      {pdfModal}
      <div className="rec-sinruta__head">
        <span className="rec-sinruta__icon" aria-hidden="true">
          <Warehouse size={18} />
        </span>

        <div className="rec-sinruta__heading">
          <h3 id="colectas-sin-ruta-titulo" className="rec-sinruta__title">
            {t("Colectas sin ruta")}
          </h3>
          {/* "Colectas sin ruta" no se explica solo. Sin esta línea el operador tiene que
              deducir por qué estas filas no están dentro de ninguna ruta. */}
          <p className="rec-sinruta__sub">
            {t("Ocurrieron hoy pero no pertenecen a ninguna ruta, así que no cuentan como paradas realizadas.")}
          </p>
        </div>

        {/* Los contadores llevan icono además de color: el color solo no comunica. */}
        <div className="rec-sinruta__counts">
          {recepciones.length > 0 && (
            <span className="rec-badge rec-badge--brand rec-badge--dot">
              {t("{{n}} recepciones en bodega", { n: recepciones.length })}
            </span>
          )}
          {sinParada.length > 0 && (
            <span className="rec-badge rec-badge--warning rec-badge--dot">
              {t("{{n}} sin parada identificada", { n: sinParada.length })}
            </span>
          )}
        </div>
      </div>

      <div className="rec-table-wrap">
        <div className="rec-list" role="table" aria-label={t("Colectas sin ruta")}>
          <div className="rec-list__head" role="row">
            <div className="rec-list__hcell" role="columnheader">{t("Comercio")}</div>
            <div className="rec-list__hcell" role="columnheader">{t("Tipo")}</div>
            <div className="rec-list__hcell" role="columnheader">{t("Entregó")}</div>
            <div className="rec-list__hcell" role="columnheader">{t("Recibió")}</div>
            <div className="rec-list__hcell" role="columnheader">{t("Remito")}</div>
          </div>

          {recepciones.map((r) => (
            <div className="rec-list__row" role="row" key={r.pickupId}>
              <div className="rec-list__cell" role="cell">
                <div className="rec-cell-strong">{r.comercioNombre || "—"}</div>
              </div>
              <div className="rec-list__cell" role="cell">
                <span className="rec-badge rec-badge--brand rec-badge--dot">
                  {t("Recepción en bodega")}
                </span>
              </div>
              {/* `entregadoPor` viene del comercio; `registradoPor` es el supervisor que
                  la recibió. Confundirlos es cómo nació este bug. */}
              <div className="rec-list__cell rec-cell-muted" role="cell">
                {r.entregadoPor?.fullName || "—"}
                {r.entregadoPor?.rut && <div className="rec-cell-muted">{r.entregadoPor.rut}</div>}
              </div>
              <div className="rec-list__cell rec-cell-muted" role="cell">
                {r.registradoPor?.nombre || "—"}
              </div>
              <div className="rec-list__cell" role="cell">
                <RetiroCell
                  pickups={[{ _id: r.pickupId, correlative: r.correlative, date: r.fecha, pdfUrl: r.pdfUrl }]}
                  pdfLink={pdfLink}
                />
              </div>
            </div>
          ))}

          {sinParada.map((s) => (
            <div className="rec-list__row" role="row" key={s._id}>
              <div className="rec-list__cell" role="cell">
                <div className="rec-cell-muted">—</div>
              </div>
              <div className="rec-list__cell" role="cell">
                <span className="rec-badge rec-badge--warning rec-badge--dot">
                  {t("Sin parada identificada")}
                </span>
              </div>
              <div className="rec-list__cell rec-cell-muted" role="cell">—</div>
              <div className="rec-list__cell rec-cell-muted" role="cell">
                {s.registradoPor?.nombre || "—"}
              </div>
              <div className="rec-list__cell" role="cell">
                <RetiroCell pickups={[s]} pdfLink={pdfLink} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// `pdfLink` viene de AdminRecolecciones: un único visor de PDF para toda la vista.
function InstanciaGrupo({ inst, isCollapsed, onToggle, onEditPlantilla, onDeletePlantilla, pdfLink }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const realizadas = inst.paradas.filter((p) => p.estado === "ejecutada").length;
  // Las anuladas salen del denominador del avance: el repartidor no tiene nada que hacer
  // ahí. Con 1 parada anulada, "0/1" diría que le falta trabajo que ya resolvió.
  const anuladas = inst.paradas.filter((p) => p.estado === "anulada").length;
  const total = inst.paradas.length;
  // Paradas que el repartidor todavía tiene que resolver. Una anulada ya se resolvió
  // —llegó y no había pedidos—, así que no cuenta como trabajo pendiente.
  const exigibles = total - anuladas;
  // Stats agregados a partir de los campos por parada. El back todavía no
  // expone bultos/pedidos/peso/cobrar/pagar por instancia — mostramos null
  // y caemos a "—" en el Stat para dejar la estructura visible y lista para
  // cuando el back los entregue.
  const sumOrNull = (key) => {
    // Los valores por parada pueden llegar como strings ("12", "3500") o
    // vacíos (""/null) desde el back. Coercemos con Number y descartamos NaN.
    // Mantiene null si NINGUNA parada aporta el campo, para caer a "—".
    const vals = inst.paradas
      .map((p) => p[key])
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v));
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) : null;
  };
  const totalBultos = sumOrNull("bultosEsperados");
  const totalPedidos = sumOrNull("pedidosEsperados");
  const totalPeso = sumOrNull("pesoEsperadoKg");
  const totalCobrar = sumOrNull("valorCobrarCliente");
  const totalPagar = sumOrNull("valorPagarRepartidor");
  const margen = totalCobrar != null && totalPagar != null ? totalCobrar - totalPagar : null;
  const fmtCount = (v) => (v == null ? "—" : formatNumberCL(v));
  const fmtCLP = (v) => (v == null ? "—" : `$${formatNumberCL(v)}`);

  return (
    <div className="rec-card rec-card--flush rec-group">
      <div className="rec-group__head">
        <button className="rec-group__toggle" onClick={onToggle}>
          <span className="rec-group__chev">
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
          <div className="rec-group__heading">
            <div className="rec-group__title-row">
              <RouteIcon size={16} className="rec-ico-brand" />
              <span className="rec-group__name">{inst.nombre}</span>
              <span className="rec-badge rec-badge--neutral">
                {total} {total !== 1 ? t("paradas") : t("parada")}
              </span>
              {exigibles > 0 && (
                <span className="rec-badge rec-badge--success rec-badge--dot">
                  {realizadas}/{exigibles} {t("realizadas")}
                </span>
              )}
              {anuladas > 0 && (
                <span className="rec-badge rec-badge--muted rec-badge--dot">
                  {anuladas} {anuladas !== 1 ? t("anuladas") : t("anulada")}
                </span>
              )}
            </div>
            <div className="rec-group__meta">
              {inst.frecuencia && (
                <span className="rec-group__meta-item">
                  <Repeat size={12} />
                  {t(inst.frecuencia)}
                </span>
              )}
            </div>
          </div>
        </button>
        {/* Stats inline del mockup: pedidos est. / bultos / peso est. / cobrar
            / pagar / margen. Los datos no vienen aún del back para las
            instancias del día — `sumOrNull` devuelve null si ningún parada
            tiene el campo, y cae a "—" visual hasta que esté disponible. */}
        <div className="rec-group__stats">
          <Stat label={t("Pedidos est.")} value={fmtCount(totalPedidos)} />
          <Stat label={t("Bultos")} value={fmtCount(totalBultos)} />
          <Stat label={t("Peso est.")} value={totalPeso == null ? "—" : `${formatNumberCL(totalPeso)} kg`} />
          <Stat label={t("Cobrar")} value={fmtCLP(totalCobrar)} tone="brand" />
          <Stat label={t("Pagar")} value={fmtCLP(totalPagar)} tone="warning" />
          <Stat
            label={t("Margen")}
            value={fmtCLP(margen)}
            tone={margen == null ? undefined : margen >= 0 ? "success" : "error"}
          />
        </div>
        <div className="rec-group__driver">
          <div className="rec-group__driver-block">
            {inst.chofer ? (
              <>
                <span className="rec-avatar rec-avatar--sm">{iniciales(inst.chofer)}</span>
                <div className="rec-group__driver-info">
                  <span className="rec-group__driver-name">{inst.chofer}</span>
                  {inst.choferEmail && <span className="rec-group__driver-email">{inst.choferEmail}</span>}
                </div>
              </>
            ) : (
              <span className="rec-group__driver-none">
                <UserIcon size={14} /> {t("Sin asignar")}
              </span>
            )}
          </div>
          {/* Acciones: editar la ruta (abre el drawer de plantilla) o eliminarla. */}
          <div className="rec-group__actions">
            <button
              type="button"
              className="rec-group__action"
              onClick={() => onEditPlantilla?.(inst)}
              title={t("Editar esta ruta")}
            >
              <Pencil size={12} /> {t("Editar")}
            </button>
            <span className="rec-group__action-sep">·</span>
            <button
              type="button"
              className="rec-group__action rec-group__action--danger"
              onClick={() => onDeletePlantilla?.(inst)}
              title={t("Eliminar esta ruta")}
            >
              <Trash2 size={12} /> {t("Eliminar")}
            </button>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        isMobile ? (
          <div className="rec-mobile-cards">
            {inst.paradas.map((p) => (
              <div className="rec-mcard" key={p.id}>
                <div className="rec-mcard__top">
                  <div className="rec-cell-strong">{p.comercio}</div>
                  <EstadoBadge estado={p.estado} t={t} />
                </div>
                <div className="rec-mcard__body">
                  <div className="rec-mrow rec-mrow--block">
                    <span className="rec-mrow__k">{t("Dirección")}</span>
                    <span className="rec-mrow__v">
                      <span className="rec-cell-strong">{p.direccion || "—"}</span>
                      {p.comuna && <span className="rec-cell-muted">{p.comuna}</span>}
                    </span>
                  </div>
                  <div className="rec-mrow">
                    <span className="rec-mrow__k">{t("Ventana")}</span>
                    <span className="rec-cell-muted">
                      {hhmm(p.ventanaDesde) && hhmm(p.ventanaHasta)
                        ? `${hhmm(p.ventanaDesde)}–${hhmm(p.ventanaHasta)}`
                        : "—"}
                    </span>
                  </div>
                  <div className="rec-mrow">
                    <span className="rec-mrow__k">{t("Bultos")}</span>
                    <span className="rec-cell-muted">
                      {p.bultosEsperados !== "" && p.bultosEsperados != null ? p.bultosEsperados : "—"}
                    </span>
                  </div>
                  <div className="rec-mrow">
                    <span className="rec-mrow__k">{t("Cobrar")}</span>
                    <span className="rec-cell-muted">
                      {Number(p.valorCobrarCliente) > 0 ? formatCLP(Number(p.valorCobrarCliente)) : "—"}
                    </span>
                  </div>
                  <div className="rec-mrow rec-mrow--block">
                    <span className="rec-mrow__k">{t("Retiro")}</span>
                    <RetiroCell pickups={p.pickups} pdfLink={pdfLink} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rec-table-wrap">
            <div className="rec-list" role="table" aria-label={inst.nombre}>
              <div className="rec-list__head" role="row">
                <div className="rec-list__hcell" role="columnheader">{t("Comercio")}</div>
                <div className="rec-list__hcell" role="columnheader">{t("Dirección")}</div>
                <div className="rec-list__hcell" role="columnheader">{t("Ventana")}</div>
                <div className="rec-list__hcell" role="columnheader">{t("Bultos")}</div>
                <div className="rec-list__hcell" role="columnheader">{t("Cobrar")}</div>
                <div className="rec-list__hcell" role="columnheader">{t("Estado")}</div>
                <div className="rec-list__hcell" role="columnheader">{t("Retiro")}</div>
              </div>
              {inst.paradas.map((p) => (
                <div className="rec-list__row" role="row" key={p.id}>
                  <div className="rec-list__cell" role="cell">
                    <div className="rec-cell-strong">{p.comercio}</div>
                  </div>
                  <div className="rec-list__cell rec-cell-addr" role="cell">
                    <div className="rec-cell-strong">{p.direccion || "—"}</div>
                    {p.comuna && <div className="rec-cell-muted">{p.comuna}</div>}
                  </div>
                  <div className="rec-list__cell rec-cell-muted" role="cell">
                    {hhmm(p.ventanaDesde) && hhmm(p.ventanaHasta)
                      ? `${hhmm(p.ventanaDesde)}–${hhmm(p.ventanaHasta)}`
                      : "—"}
                  </div>
                  <div className="rec-list__cell rec-cell-muted" role="cell">
                    {p.bultosEsperados !== "" && p.bultosEsperados != null ? p.bultosEsperados : "—"}
                  </div>
                  <div className="rec-list__cell rec-cell-muted" role="cell">
                    {Number(p.valorCobrarCliente) > 0 ? formatCLP(Number(p.valorCobrarCliente)) : "—"}
                  </div>
                  <div className="rec-list__cell" role="cell">
                    <EstadoBadge estado={p.estado} t={t} />
                  </div>
                  <div className="rec-list__cell" role="cell">
                    <RetiroCell pickups={p.pickups} pdfLink={pdfLink} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rec-stat">
      <div className="rec-stat__label">{label}</div>
      <div className={`rec-stat__value ${tone ? `rec-stat__value--${tone}` : ""}`}>{value}</div>
    </div>
  );
}

// ── Switch de vista: Recolecciones (nueva) ⇄ Administrar retiros (vieja) ──
// Segmented control accesible (role=tablist). El activo usa el primario de marca
// igual que rec-btn--primary; el inactivo es texto atenuado sobre el track.
// Navegable con flechas ←/→.
function ViewSwitch({ vista, setVista, t }) {
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      setVista(vista === "nueva" ? "vieja" : "nueva");
    }
  };
  return (
    <div className="rec-viewswitch" role="tablist" aria-label={t("Cambiar vista")} onKeyDown={onKeyDown}>
      <button
        type="button"
        role="tab"
        id="rec-tab-nueva"
        aria-selected={vista === "nueva"}
        tabIndex={vista === "nueva" ? 0 : -1}
        className={`rec-viewswitch__opt ${vista === "nueva" ? "is-active" : ""}`}
        onClick={() => setVista("nueva")}
      >
        <Layers size={15} aria-hidden />
        {t("Recolecciones")}
      </button>
      <button
        type="button"
        role="tab"
        id="rec-tab-vieja"
        aria-selected={vista === "vieja"}
        tabIndex={vista === "vieja" ? 0 : -1}
        className={`rec-viewswitch__opt ${vista === "vieja" ? "is-active" : ""}`}
        onClick={() => setVista("vieja")}
        title={t("Vista anterior de administración de retiros")}
      >
        <History size={15} aria-hidden />
        {t("Administrar retiros")}
        <span className="rec-viewswitch__tag">{t("vista vieja")}</span>
      </button>
    </div>
  );
}

// ─────────────────────── Mapa: refit dinámico al filtrar ─────────────────────
// Reajusta el viewport del mapa cuando cambia el conjunto de pines visibles
// (por toggles de capas o filtro de ruta). Mismo patrón que ActividadRouteMap.
function FitToPins({ pins }) {
  const map = useMap();
  // Clave estable basada en el set de pines (sin coords flotantes en string para
  // no inflar la key; un hash simple por ids basta).
  const key = useMemo(() => pins.map((p) => p.id).join("|"), [pins]);
  useEffect(() => {
    if (!map || pins.length === 0) return;
    const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng]));
    map.flyToBounds(bounds.pad(0.18), { duration: 0.4, maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

// ─────────────────────── Panel flotante: capas + ruta ─────────────────────
function MapControlsPanel({
  t, open, onToggle, layers, onLayerToggle, counts, routeOptions,
  routeId, onRouteChange, visibleCount, totalCount,
}) {
  // Los tres estados de parada, todos reales. El swatch usa el mismo color que los
  // pines del mapa (tokens `--status-*`) para que leyenda y marcadores se lean como
  // un solo sistema, y la FORMA distingue lo que el color no puede: pendiente es un
  // aro hueco y anulada un aro punteado, porque comparten tono neutral.
  const items = [
    // "Pendientes" (no "Programadas"): mismo nombre que la tabla y la leyenda.
    { key: "programada", label: t("Pendientes"), color: ESTADO_META.programada.color, hollow: true },
    { key: "ejecutada",  label: t("Realizadas"), color: ESTADO_META.ejecutada.color },
    { key: "anulada",    label: t("Anuladas"),   color: ESTADO_META.anulada.color, hollow: true, slash: true },
  ];

  return (
    <div className={`rec-mapctl ${open ? "is-open" : ""}`}>
      {!open ? (
        <button
          type="button"
          className="rec-mapctl__pill"
          onClick={onToggle}
          aria-expanded={false}
          title={t("Capas y filtros del mapa")}
        >
          <Layers size={14} />
          <span>{t("Capas")}</span>
          {(routeId || visibleCount !== totalCount) && (
            <span className="rec-mapctl__dot" aria-label={t("Filtros activos")} />
          )}
        </button>
      ) : (
        <div className="rec-mapctl__panel" role="dialog" aria-label={t("Capas y filtros del mapa")}>
          <div className="rec-mapctl__head">
            <span className="rec-mapctl__title">
              <Layers size={14} /> {t("Capas")}
            </span>
            <button
              type="button"
              className="rec-mapctl__close"
              onClick={onToggle}
              aria-label={t("Cerrar")}
            >
              <X size={14} />
            </button>
          </div>

          <div className="rec-mapctl__section">
            <div className="rec-mapctl__sectionhead">{t("Estados")}</div>
            <ul className="rec-mapctl__list">
              {items.map((it) => {
                const on = !!layers[it.key];
                const n = counts[it.key] ?? 0;
                return (
                  <li key={it.key} className={`rec-mapctl__row ${it.ghost ? "is-ghost" : ""}`}>
                    <label className="rec-mapctl__toggle">
                      <input
                        type="checkbox"
                        checked={on && !it.ghost}
                        disabled={it.ghost}
                        onChange={() => !it.ghost && onLayerToggle(it.key)}
                      />
                      <span
                        className={`rec-mapctl__swatch ${it.hollow ? "is-hollow" : ""} ${it.slash ? "is-slash" : ""}`}
                        style={
                          it.hollow
                            // `color` alimenta el `currentColor` de la barra de anulada;
                            // sin esto tomaría el gris de borde por defecto del swatch.
                            ? { borderColor: it.color, color: it.color }
                            : { background: it.color }
                        }
                        aria-hidden
                      />
                      <span className="rec-mapctl__label">{it.label}</span>
                    </label>
                    <span className="rec-mapctl__count">
                      {it.ghost ? "—" : n}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="rec-mapctl__section">
            <div className="rec-mapctl__sectionhead">{t("Ruta")}</div>
            <div className="rec-mapctl__select">
              <select
                value={routeId}
                onChange={(e) => onRouteChange(e.target.value)}
                aria-label={t("Filtrar el mapa por ruta")}
              >
                <option value="">{t("Todas las rutas")} ({routeOptions.length})</option>
                {routeOptions.map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
              </select>
              <ChevronDown size={14} className="rec-mapctl__selectchev" aria-hidden />
            </div>
            {routeId && (
              <button
                type="button"
                className="rec-mapctl__clear"
                onClick={() => onRouteChange("")}
              >
                <X size={12} /> {t("Quitar filtro de ruta")}
              </button>
            )}
          </div>

          <div className="rec-mapctl__foot">
            {t("Mostrando {{v}} / {{n}} paradas", { v: visibleCount, n: totalCount })}
          </div>
        </div>
      )}
    </div>
  );
}
